/**
 * OpenInvoicesQueue — issue #29 AC4
 *
 * The "settleable queue" that surfaces every booking the staff still
 * owes money on. Becky's two-track Settle Later workflow (issue #34)
 * lands open invoices here — the staff sees a single list, picks the
 * payment method per invoice, and processes the charge inline.
 *
 * Server contract:
 *   GET /api/v2/admin/payments/open-invoices → AdminOpenInvoice[]
 *   Server filters to bookings.status in (checked_out, completed) with
 *   no matching completed Payment row, scoped to the tenant.
 *
 * UX:
 *   - Each row shows: customer name + phone, service + date, amount,
 *     and three payment-method buttons (Card on File, Tap-to-Pay, Manual).
 *   - Clicking a payment-method button surfaces the parent-passed
 *     `onSelectInvoice(invoice, method)` callback so the parent can
 *     switch its Process Payment tab to the right flow with the right
 *     bookingId + customerId pre-loaded.
 *   - A pull-to-refresh equivalent (Refresh button) re-queries after
 *     a settle completes.
 *
 * Acceptance criteria:
 *   AC4: Given there are open invoices, when I open the Open Invoices
 *        view, then I see them in a settleable queue with payment
 *        method options per invoice.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  adminPaymentsApi,
  type AdminOpenInvoice,
} from '../../lib/api.js';

export type PaymentMethod = 'card-on-file' | 'tap-to-pay' | 'manual';

const ALL_METHODS: readonly PaymentMethod[] = [
  'card-on-file',
  'tap-to-pay',
  'manual',
];

type OpenInvoicesQueueProps = {
  readonly apiUrl: string;
  readonly onSelectInvoice: (
    invoice: AdminOpenInvoice,
    method: PaymentMethod,
  ) => void;
  /** Optional refresh signal — parent bumps a number to force a re-fetch. */
  readonly refreshKey?: number;
  /**
   * #522 — the gateway-resolved method set (the queue's per-row buttons
   * are a second method picker and must not offer a method the tenant's
   * gateway can't run, e.g. Tap to Pay on the Authorize.net rail).
   * Defaults to all three for standalone/legacy mounts.
   */
  readonly methods?: readonly PaymentMethod[];
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; invoices: AdminOpenInvoice[] };

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function OpenInvoicesQueue({
  apiUrl,
  onSelectInvoice,
  refreshKey,
  methods = ALL_METHODS,
}: OpenInvoicesQueueProps): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    const result = await adminPaymentsApi.getOpenInvoices(apiUrl, { limit: 100 });
    if (result.error || !result.data) {
      setState({
        kind: 'error',
        message: result.error ?? 'Could not load open invoices',
      });
      return;
    }
    setState({ kind: 'ready', invoices: result.data.invoices });
  }, [apiUrl]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  return (
    <section
      className="ferro-card"
      aria-label="Open invoices queue"
      data-testid="open-invoices-queue"
    >
      <header className="ferro-card__header">
        <h3 className="ferro-section__subtitle">Open invoices</h3>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={() => void load()}
          disabled={state.kind === 'loading'}
          data-testid="open-invoices-refresh"
        >
          Refresh
        </button>
      </header>

      {state.kind === 'loading' ? (
        <p
          className="ferro-muted"
          role="status"
          data-testid="open-invoices-status"
        >
          Loading open invoices…
        </p>
      ) : null}

      {state.kind === 'error' ? (
        <div
          className="ferro-dashboard__error"
          role="alert"
          data-testid="open-invoices-error"
        >
          <p>{state.message}</p>
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={() => void load()}
            data-testid="open-invoices-retry"
          >
            Retry
          </button>
        </div>
      ) : null}

      {state.kind === 'ready' && state.invoices.length === 0 ? (
        <p
          className="ferro-muted"
          data-testid="open-invoices-empty"
        >
          No open invoices. All checked-out bookings have been settled.
        </p>
      ) : null}

      {state.kind === 'ready' && state.invoices.length > 0 ? (
        <ul
          className="ferro-table__body"
          data-testid="open-invoices-list"
          style={{ listStyle: 'none', padding: 0, margin: 0 }}
        >
          {state.invoices.map((invoice) => (
            <li
              key={invoice.id}
              className="ferro-table__row"
              data-testid={`open-invoice-${invoice.id}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0.5rem',
                padding: '0.75rem 0',
                borderBottom: '1px solid var(--ferro-color-border, #eee)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: '1rem',
                }}
              >
                <div>
                  <strong data-testid={`open-invoice-${invoice.id}-customer`}>
                    {invoice.customerName}
                  </strong>
                  {invoice.customerPhone ? (
                    <span className="ferro-muted"> · {invoice.customerPhone}</span>
                  ) : null}
                </div>
                <strong data-testid={`open-invoice-${invoice.id}-amount`}>
                  {formatMoney(invoice.totalCents)}
                </strong>
              </div>
              <div className="ferro-muted">
                {invoice.serviceTypeName} · {formatDate(invoice.serviceDate)}
              </div>
              {invoice.notes ? (
                <div
                  className="ferro-muted"
                  data-testid={`open-invoice-${invoice.id}-notes`}
                >
                  Note: {invoice.notes}
                </div>
              ) : null}
              <div
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  flexWrap: 'wrap',
                }}
              >
                {methods.includes('card-on-file') ? (
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--primary"
                    onClick={() => onSelectInvoice(invoice, 'card-on-file')}
                    data-testid={`open-invoice-${invoice.id}-card-on-file`}
                  >
                    Card on file
                  </button>
                ) : null}
                {methods.includes('tap-to-pay') ? (
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--primary"
                    onClick={() => onSelectInvoice(invoice, 'tap-to-pay')}
                    data-testid={`open-invoice-${invoice.id}-tap-to-pay`}
                  >
                    Tap to pay
                  </button>
                ) : null}
                {methods.includes('manual') ? (
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--ghost"
                    onClick={() => onSelectInvoice(invoice, 'manual')}
                    data-testid={`open-invoice-${invoice.id}-manual`}
                  >
                    Manual entry
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
